// Row shapes the portal reads. JobRow / InstallRow mirror services/api/src/rows.ts;
// the installer extras are the portal columns crm.v_job carries (address, product,
// party). Kept loose like the API: a view gains a column without a contract bump.
export interface Origin { data_origin: "live" | "synced" | "test"; test_set_id: string | null }

export type PortalState = "offered" | "accepted" | "scheduled" | "installing" | "done";

export interface InstallerJob extends Origin {
  ref: string;
  name: string | null;
  suburb: string | null;
  effective_stage: string | null;
  stage_group: string | null;
  install_gate: string | null;
  gate?: string | null;
  install_next_date: string | null;
  created_at: string;
  updated_at: string;
  address?: string | null;
  product?: string | null;
  area_m2?: number | null;
  stairs?: number | null;
  customer?: string | null;
  customer_phone?: string | null;
  /** portal.installer_party id; null = a Team Artemis crew */
  party_id?: string | null;
  party_name?: string | null;
  crew?: string | null;
  installation_id?: string | null;
  days?: { id: string; date: string; team?: string | null; task?: string | null; done_at?: string | null }[];
  portal_state?: PortalState | null;
  answer_by?: string | null;
  signed_off_at?: string | null;
  [k: string]: unknown;
}

/** Office completion is authoritative even when no installer day report exists. */
export const installationComplete = (job: InstallerJob) => job.portal_state === "done" || !!job.signed_off_at || job.gate === "Install Complete" || job.install_gate === "Install Complete";

export interface InstallDay { id?:string; date: string; crew: string | null; task: string | null; done: string | null; assignee?:{kind:string;id:string;name?:string}|null }
export interface Install extends Origin {
  id: string;
  job_ref: string;
  team: string | null;
  gate: string | null;
  next_date: string | null;
  schedule_col_count: number;
  days?: InstallDay[];
  [k: string]: unknown;
}

export interface PlanRoom { x: number; y: number; w: number; h: number; name: string; dim: string; area: string; hl?: boolean }
export interface SetOutPlan {
  rooms: PlanRoom[];
  doors: [number, number, number, number][];
  start: [number, number, number, number] | null;
  direction: "across" | "down";
}

/** What the platform's work-order page carries (projectWoHtml.ts field names), per job. */
export interface WorkOrder {
  job_ref: string;
  wo_ref: string;
  address: string;
  customer: string;
  customer_phone: string;
  start_time: string;
  parking: string | null;
  access: string | null;
  ppe: string[];
  office_contact: { name: string; phone: string };
  notes: string | null;
  materials: { item: string; detail: string | null; qty: string }[];
  pickup_orders?: { supplier: string; order: string; pickup_references: string[] }[];
  delivered_at: string | null;
  measured_at: string | null;
  rooms: { name: string; dim: string; area_m2: number }[];
  plan: SetOutPlan;
  pm?: { name: string; after_day: number };
  /** own party's labour; the API omits this for internal crews */
  labour: { line: string; qty: string; rate: string; amount: number }[] | null;
}

export interface StatementLine { job_ref: string; job: string; work: string; amount: number; state: "paid" | "pending" }
export interface Statement {
  party: { id: string | null; name: string };
  month: string;
  month_zh: string;
  owed: number;
  paid: { amount: number; at: string } | null;
  pending: number;
  lines: StatementLine[];
  statements: { id: string; total: number | null; draft: boolean }[];
  rates: { key: "rate_hybrid" | "rate_carpet" | "rate_stairs" | "rate_removal"; rate: string; per_step?: boolean; agreed: string | null }[];
}

export interface Me {
  user: { id: string; email: string | null };
  actor: { kind: "human"; id: string; role: string; org_id: string | null } | null;
  memberships: { role: string; org_id: string | null; status: string }[];
  surface: string;
  test_set_id: string | null;
  /** not on the live /api/me yet — the party name for the header */
  party?: { id: string; name: string } | null;
  installer_member?: {user_id:string;display_name:string;team_role:'manager'|'worker';can_install:boolean;preferred_language:'en'|'zh';active:boolean;phone:string|null;email:string|null}|null;
}

export interface InstallerTeamMember {kind:'member'|'request';party_id:string;user_id?:string|null;request_id?:string;display_name:string;team_role:'manager'|'worker';can_install:boolean;preferred_language:'en'|'zh';active:boolean;phone:string|null;email:string|null;state:string;delivery_state?:string}
export interface InstallerTeamWork {installation_id:string;job_ref:string;day_id:string;date:string;task:string|null;party_id:string;company:string;workers:{user_id:string;name:string}[]}
export interface InstallerPriceForm {party:{id:string;name:string;categories:string[]|null};books:{id:string;version:number;approval_status:string;submitted_at:string|null}[];config:{book_category:string;capabilities:{id:string;en:string;zh:string}[];services:{id:string;group_en:string;name_en:string;group_zh:string;name_zh:string;uom:string;inclusions:string;inclusions_zh:string}[]}}
